import React, { useState, useEffect } from 'react';
import { knowledgeBaseService } from '../../services/api';

interface KBDocument {
    id: string;
    title: string;
    source: string;
    sourceUrl: string;
    version: number;
    chunkCount: number;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface KBChunk {
    sectionRef: string;
    heading: string;
    text: string;
    summary: string;
    index: number;
}

const KnowledgeBaseAdmin: React.FC = () => {
    const [docs, setDocs] = useState<KBDocument[]>([]);
    const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
    const [docDetail, setDocDetail] = useState<{ title: string; chunks: KBChunk[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addTitle, setAddTitle] = useState('');
    const [addUrl, setAddUrl] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[] | null>(null);
    const [searching, setSearching] = useState(false);

    const fetchDocs = async () => {
        setLoading(true);
        try {
            const res = await knowledgeBaseService.listDocs();
            setDocs(res.data?.data || []);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDocs(); }, []);

    const handleAddDoc = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!addTitle.trim()) return;
        try {
            await knowledgeBaseService.addDoc({ title: addTitle, sourceUrl: addUrl });
            setAddTitle('');
            setAddUrl('');
            setShowAddForm(false);
            fetchDocs();
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to add document');
        }
    };

    const handleViewDoc = async (id: string) => {
        setSelectedDoc(id);
        try {
            const res = await knowledgeBaseService.getDoc(id);
            const data = res.data?.data;
            if (data) {
                setDocDetail({ title: data.title, chunks: data.chunks || [] });
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load document details');
        }
    };

    const handleDeleteDoc = async (id: string) => {
        if (!window.confirm('Delete this document and all its chunks?')) return;
        try {
            await knowledgeBaseService.deleteDoc(id);
            if (selectedDoc === id) { setSelectedDoc(null); setDocDetail(null); }
            fetchDocs();
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to delete document');
        }
    };

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim().length < 3) return;
        setSearching(true);
        try {
            const res = await knowledgeBaseService.search(searchQuery);
            setSearchResults(res.data?.data || []);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Search failed');
        } finally {
            setSearching(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const colors: Record<string, string> = {
            active: 'bg-green-100 text-green-800',
            pending: 'bg-yellow-100 text-yellow-800',
            error: 'bg-red-100 text-red-800',
        };
        return (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
                {status}
            </span>
        );
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Compliance Knowledge Base</h2>
                    <p className="text-sm text-gray-500 mt-1">Indexed legislation, guidance, and regulatory documents powering the Compliance Advisor</p>
                </div>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700"
                >
                    {showAddForm ? 'Cancel' : 'Add Document'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg mb-4">
                    {error}
                    <button onClick={() => setError(null)} className="ml-2 font-bold">&times;</button>
                </div>
            )}

            {showAddForm && (
                <form onSubmit={handleAddDoc} className="bg-white border border-gray-200 rounded-lg p-4 mb-6 space-y-3">
                    <input
                        type="text"
                        value={addTitle}
                        onChange={e => setAddTitle(e.target.value)}
                        placeholder="Document title (e.g. Renters' Rights Act 2025)"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        required
                    />
                    <input
                        type="url"
                        value={addUrl}
                        onChange={e => setAddUrl(e.target.value)}
                        placeholder="Source URL (e.g. https://www.legislation.gov.uk/ukpga/2025/26/contents)"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <button type="submit" className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700">
                        Index Document
                    </button>
                </form>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-white border border-gray-200 rounded-lg">
                    <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                        <h3 className="text-sm font-semibold text-gray-700">Documents ({docs.length})</h3>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="p-4 text-sm text-gray-500 text-center">Loading...</div>
                        ) : docs.length === 0 ? (
                            <div className="p-4 text-sm text-gray-500 text-center">No documents indexed yet</div>
                        ) : docs.map(doc => (
                            <div
                                key={doc.id}
                                className={`p-3 cursor-pointer hover:bg-gray-50 ${selectedDoc === doc.id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''}`}
                                onClick={() => handleViewDoc(doc.id)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{doc.title}</p>
                                        {getStatusBadge(doc.status)}
                                    </div>
                                    <button
                                        onClick={e => { e.stopPropagation(); handleDeleteDoc(doc.id); }}
                                        className="text-gray-400 hover:text-red-600 text-lg leading-none p-1 shrink-0"
                                        title="Delete document"
                                    >
                                        &times;
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1 truncate">{doc.sourceUrl || doc.source}</p>
                                <div className="flex items-center gap-3 mt-1">
                                    <span className="text-xs text-gray-400">{doc.chunkCount} chunks</span>
                                    <span className="text-xs text-gray-400">v{doc.version}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-2 space-y-4">
                    {/* Search */}
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Test search the knowledge base..."
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                        <button
                            type="submit"
                            disabled={searching || searchQuery.trim().length < 3}
                            className="bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                        >
                            {searching ? '...' : 'Search'}
                        </button>
                    </form>

                    {/* Search Results */}
                    {searchResults && (
                        <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-gray-700">Search Results</h3>
                                <button onClick={() => setSearchResults(null)} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
                            </div>
                            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                                {searchResults.length === 0 ? (
                                    <div className="p-4 text-sm text-gray-500 text-center">No relevant results found</div>
                                ) : searchResults.map((r, i) => (
                                    <div key={i} className="p-3">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs font-medium text-gray-900">{r.documentTitle}</p>
                                            <span className="text-xs text-emerald-600 font-medium">{Math.round(r.score * 100)}% match</span>
                                        </div>
                                        {r.sectionRef && <p className="text-xs text-gray-500 mt-0.5">{r.sectionRef}: {r.heading}</p>}
                                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{r.text?.substring(0, 300)}</p>
                                        {r.documentUrl && (
                                            <a href={r.documentUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 hover:underline mt-1 inline-block">
                                                Source
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Document Detail */}
                    {docDetail && (
                        <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                                <h3 className="text-sm font-semibold text-gray-700">{docDetail.title}</h3>
                            </div>
                            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                                {docDetail.chunks.length === 0 ? (
                                    <div className="p-4 text-sm text-gray-500 text-center">No chunks indexed yet. Document may still be processing.</div>
                                ) : docDetail.chunks.map((chunk, i) => (
                                    <div key={i} className="p-3">
                                        <div className="flex items-center gap-2">
                                            {chunk.sectionRef && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">{chunk.sectionRef}</span>}
                                            <span className="text-xs text-gray-500">{chunk.heading}</span>
                                        </div>
                                        <p className="text-xs text-gray-600 mt-1">{chunk.text?.substring(0, 400)}{chunk.text?.length > 400 ? '...' : ''}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {!searchResults && !docDetail && !loading && docs.length > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                            <p className="text-sm text-gray-500">Select a document from the list or search the knowledge base</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KnowledgeBaseAdmin;
